import { AxiosResponse } from 'axios';
import { OrderSearchResponse, OrderSearchCriteria, OrderAnalysisResponse, HitlOrdersResponse, ManualEditLogEntry, BackendPayloadListResponse, OrderState } from './types';
import { createApiClient, isAbortError } from 'services/apiClient';
import { performBulkDelete, BulkDeleteResult, BulkDeleteOptions } from 'services/bulkDelete';
import { API_BASE_URL } from 'config/api';
import { normalizeIntegrationType } from 'components/tenant/outbound/documentTypeRegistry';
import { withStatus } from 'components/tenantAnalysisFailure';

const apiClient = createApiClient('ORDER');

/**
 * Map a backend order payload to the frontend's `OrderState` shape.
 *
 * Supports both the new document-centric API (`document_state_id`,
 * `parsed_document`, `document_status`) and the legacy order-centric shape
 * (`order_state_id`, `parsed_order`, `order_status`) so detail fetches and the
 * HITL list endpoint can share one mapping. `tenantId` is an optional fallback
 * for payloads (e.g. list rows) that don't carry `tenant_id`.
 */
function mapOrderStateFromApi(raw: any, tenantId?: string): OrderState {
  return {
    order_state_id: raw.document_state_id || raw.order_state_id,
    tenant_id: raw.tenant_id || tenantId,
    order_number: (raw.parsed_document || raw.parsed_order)?.order_number,
    order_status: raw.document_status || raw.order_status,
    order_source_type: raw.document_source_type || raw.order_source_type,
    order_items_matched: raw.order_items_matched,
    order_has_been_sent: raw.has_been_sent ?? raw.order_has_been_sent,
    is_processing: raw.is_processing ?? false,
    order_has_work_description: raw.order_has_work_description,
    work_description: raw.work_description || [],
    work_description_entries: raw.work_description_entries || [],
    order_backend_id: raw.backend_id || raw.order_backend_id,
    backend_type: raw.backend_type ?? null,
    backend_system_id: raw.backend_system_id ?? null,
    backend_status_log: raw.backend_status_log ?? [],
    retry_count: raw.retry_count,
    max_retry_reached: raw.max_retry_reached,
    last_error_message: raw.last_error_message,
    error_history: raw.error_history,
    parsed_order: raw.parsed_document || raw.parsed_order,
    edited_parsed_order: raw.edited_parsed_document || null,
    parent_process_state_id: raw.parent_process_state_id,
    parent_process_type: raw.parent_process_type || null,
    email: raw.email || null,
    email_eml_path: raw.order_email_eml_path || raw.email_eml_path,
    pdf_path: raw.doc_path || raw.pdf_path,
    phone_number: raw.phone_number || null,
    processing_result: raw.processing_result,
    matched_customer: raw.processing_result?.matched_customer || raw.matched_customer || null,
    created_at_date: raw.created_at_date,
    hitl_required: raw.hitl_required,
    hitl_reason: raw.hitl_reason,
    hitl_rule_ids: raw.hitl_rule_ids,
    hitl_approved: raw.hitl_approved,
    hitl_approved_at: raw.hitl_approved_at,
    hitl_approved_by: raw.hitl_approved_by,
    manual_edit_log: raw.manual_edit_log || [],
    hitl_evaluation_history: raw.hitl_evaluation_history || [],
    hitl_override: raw.hitl_override ?? null,
  };
}

export default class OrderService {
  static async searchOrders(tenantId: string, criteria: OrderSearchCriteria): Promise<OrderSearchResponse> {
    try {
      console.log('📦 [ORDER] Searching orders:', {
        tenantId,
        criteria,
        url: `${API_BASE_URL}/business-documents/orders/search/${tenantId}`,
        method: 'GET'
      });
      
      const params = new URLSearchParams();
      
      // Add query parameters
      if (criteria.order_status) params.append('order_status', criteria.order_status);
      if (criteria.created_at_start) params.append('created_at_start', criteria.created_at_start);
      if (criteria.created_at_end) params.append('created_at_end', criteria.created_at_end);
      if (criteria.order_number) params.append('order_number', criteria.order_number);
      if (criteria.order_state_id) params.append('order_state_id', criteria.order_state_id);
      if (criteria.order_items_matched != null) params.append('order_items_matched', criteria.order_items_matched.toString());
      if (criteria.order_has_been_sent != null) params.append('order_has_been_sent', criteria.order_has_been_sent.toString());
      if (criteria.parent_process_state_id) params.append('parent_process_state_id', criteria.parent_process_state_id);
      if (criteria.customer_number) params.append('customer_number', criteria.customer_number);
      if (criteria.customer_name) params.append('customer_name', criteria.customer_name);
      if (criteria.matched_customer_number) params.append('matched_customer_number', criteria.matched_customer_number);
      if (criteria.order_backend_id) params.append('order_backend_id', criteria.order_backend_id);
      if (criteria.max_retry_reached != null) params.append('max_retry_reached', criteria.max_retry_reached.toString());
      if (criteria.has_errors != null) params.append('has_errors', criteria.has_errors.toString());
      if (criteria.days_back) params.append('days_back', criteria.days_back.toString());
      if (criteria.limit) params.append('limit', criteria.limit.toString());
      if (criteria.hitl_required != null) params.append('hitl_required', criteria.hitl_required.toString());
      if (criteria.hitl_approved != null) params.append('hitl_approved', criteria.hitl_approved.toString());
      if (criteria.hitl_reason) params.append('hitl_reason', criteria.hitl_reason);
      if (criteria.hitl_rule_id) params.append('hitl_rule_id', criteria.hitl_rule_id);
      if (criteria.hitl_approved_by) params.append('hitl_approved_by', criteria.hitl_approved_by);
      if (criteria.hitl_approved_at_start) params.append('hitl_approved_at_start', criteria.hitl_approved_at_start);
      if (criteria.hitl_approved_at_end) params.append('hitl_approved_at_end', criteria.hitl_approved_at_end);
      if (criteria.overridden != null) params.append('overridden', criteria.overridden.toString());
      
      const url = `/business-documents/orders/search/${tenantId}${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response: AxiosResponse<OrderSearchResponse> = await apiClient.get(url);
      
      console.log('✅ [ORDER] Order search response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_id: response.data.tenant_id,
          total_results: response.data.total_results,
          result_count: response.data.results?.length || 0
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [ORDER] Error searching orders:', {
        tenantId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to search orders');
    }
  }

  static async getOrderStateDetail(tenantId: string, orderStateId: string): Promise<any> {
    try {
      console.log('📦 [ORDER] Fetching order state detail:', {
        tenantId,
        orderStateId,
        url: `${API_BASE_URL}/business-documents/orders/state/${tenantId}/${orderStateId}`,
        method: 'GET'
      });
      
      const response: AxiosResponse<any> = await apiClient.get(`/business-documents/orders/state/${tenantId}/${orderStateId}`);

      // Map the backend response to match the frontend's expected structure
      // Support both old and new API response formats for backwards compatibility
      const mappedData = mapOrderStateFromApi(response.data, tenantId);

      console.log('✅ [ORDER] Order state detail response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_id: tenantId,
          order_state_id: mappedData.order_state_id,
          order_status: mappedData.order_status,
          order_number: mappedData.parsed_order?.order_number,
          matched_order_items: mappedData.processing_result?.matched_order_items?.length || 0
        }
      });
      
      return mappedData;
    } catch (error: any) {
      console.error('❌ [ORDER] Error fetching order state detail:', {
        tenantId,
        orderStateId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to fetch order state detail');
    }
  }

  static async sendOrder(tenantId: string, orderStateId: string, debug: boolean = false): Promise<any> {
    try {
      console.log('📤 [ORDER] Sending order:', {
        tenantId,
        orderStateId,
        debug,
        url: `${API_BASE_URL}/business-documents/orders/send/${tenantId}/${orderStateId}`,
        method: 'POST'
      });
      
      const params = new URLSearchParams();
      if (debug) {
        params.append('debug', 'true');
      }
      
      const url = `/business-documents/orders/send/${tenantId}/${orderStateId}${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response: AxiosResponse<any> = await apiClient.post(url);
      
      console.log('✅ [ORDER] Send order response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [ORDER] Error sending order:', {
        tenantId,
        orderStateId,
        debug,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to send order');
    }
  }

  /**
   * Take an order out of play without deleting it: it stops being sendable and
   * leaves the review queue, but its error history, send log and edit log all
   * survive — that history is what someone investigating the failure needs.
   *
   * The backend refuses (400) an order that has already been sent (the ERP
   * holds it; cancel it there) or one mid-send, so the caller's error message
   * must survive to the screen rather than being flattened.
   */
  static async discardOrder(tenantId: string, orderStateId: string, reason?: string): Promise<any> {
    try {
      const params = new URLSearchParams();
      if (reason && reason.trim()) {
        params.append('reason', reason.trim());
      }
      const url = `/business-documents/orders/discard/${tenantId}/${orderStateId}${params.toString() ? `?${params.toString()}` : ''}`;

      console.log('🗑️ [ORDER] Discarding order:', {
        tenantId,
        orderStateId,
        url: `${API_BASE_URL}${url}`,
        method: 'POST'
      });

      const response: AxiosResponse<any> = await apiClient.post(url);

      console.log('✅ [ORDER] Discard order response:', {
        status: response.status,
        data: response.data
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ [ORDER] Error discarding order:', {
        tenantId,
        orderStateId,
        status: error.response?.status,
        data: error.response?.data
      });
      // Keep the server's reason: "already sent" and "mid-send" are different
      // refusals and the operator needs to know which one they hit.
      const detail = error.response?.data?.detail;
      throw new Error(typeof detail === 'string' && detail ? detail : (error.message || 'Failed to discard order'));
    }
  }

  static async updateOrder(tenantId: string, orderStateId: string, orderData: any): Promise<any> {
    try {
      console.log('📝 [ORDER] Updating order:', {
        tenantId,
        orderStateId,
        url: `${API_BASE_URL}/business-documents/orders/state/${tenantId}/${orderStateId}`,
        method: 'PUT'
      });
      
      const url = `/business-documents/orders/state/${tenantId}/${orderStateId}`;
      
      const response: AxiosResponse<any> = await apiClient.put(url, orderData);
      
      console.log('✅ [ORDER] Update order response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [ORDER] Error updating order:', {
        tenantId,
        orderStateId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to update order');
    }
  }

  static async getOrderAnalysis(tenantId: string, days: number = 30, signal?: AbortSignal): Promise<OrderAnalysisResponse> {
    try {
      console.log('📊 [ORDER] Getting order analysis:', {
        tenantId,
        days,
        url: `${API_BASE_URL}/business-documents/orders/analysis/${tenantId}`,
        method: 'GET'
      });
      
      const params = new URLSearchParams();
      params.append('days', days.toString());
      
      const url = `/business-documents/orders/analysis/${tenantId}?${params.toString()}`;
      
      const response: AxiosResponse<OrderAnalysisResponse> = await apiClient.get(url, { signal });
      
      console.log('✅ [ORDER] Order analysis response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_id: response.data.tenant_id,
          total_orders: response.data.total_orders,
          date_range: response.data.date_range,
          error_rate: response.data.statistics.error_statistics.error_rate_percentage
        }
      });
      
      return response.data;
    } catch (error: any) {
      // An abort is the caller's own doing — surface it unwrapped so they can
      // recognise it, and don't log it as a failure.
      if (isAbortError(error)) throw error;
      console.error('❌ [ORDER] Error getting order analysis:', {
        tenantId,
        days,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw withStatus('Failed to get order analysis', error);
    }
  }

  static async getHitlOrders(tenantId: string, includeApproved: boolean = false, limit: number = 100): Promise<HitlOrdersResponse> {
    try {
      console.log('📦 [ORDER] Fetching HITL orders:', {
        tenantId,
        includeApproved,
        limit,
        url: `${API_BASE_URL}/business-documents/orders/hitl/${tenantId}`,
        method: 'GET'
      });
      
      const params = new URLSearchParams();
      params.append('include_approved', includeApproved.toString());
      params.append('limit', limit.toString());
      
      const url = `/business-documents/orders/hitl/${tenantId}?${params.toString()}`;
      
      const response: AxiosResponse<HitlOrdersResponse> = await apiClient.get(url);

      console.log('✅ [ORDER] HITL orders response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_id: response.data.tenant_id,
          count: response.data.count,
          orders_count: response.data.orders?.length || 0
        }
      });

      // Map each order from the document-centric API shape to the frontend's
      // expected OrderState shape (row ids, status, parsed_order, etc.).
      return {
        ...response.data,
        orders: (response.data.orders ?? []).map((order) => mapOrderStateFromApi(order, tenantId)),
      };
    } catch (error: any) {
      console.error('❌ [ORDER] Error fetching HITL orders:', {
        tenantId,
        includeApproved,
        limit,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to fetch HITL orders');
    }
  }

  static async reprocessOrder(tenantId: string, orderStateId: string, manualEditLog?: ManualEditLogEntry[]): Promise<any> {
    try {
      const url = `/business-documents/orders/reprocess/${tenantId}/${orderStateId}`;
      
      // Build request body: send manual_edit_log if provided
      const requestBody: any = {};
      if (manualEditLog && manualEditLog.length > 0) {
        requestBody.manual_edit_log = manualEditLog;
      }
      
      const response: AxiosResponse<any> = await apiClient.post(url, requestBody);
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [ORDER] Error reprocessing order:', {
        tenantId,
        orderStateId,
        manualEditLog,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to reprocess order');
    }
  }

  static async listBackendPayloads(tenantId: string, orderStateId: string): Promise<BackendPayloadListResponse> {
    try {
      const response: AxiosResponse<BackendPayloadListResponse> = await apiClient.get(
        `/business-documents/orders/backend-payloads/${tenantId}/${orderStateId}`
      );
      const data = response.data;
      if (data.payloads) {
        data.payloads = data.payloads.map(f => ({
          ...f,
          backend_type: normalizeIntegrationType(f.backend_type),
        }));
      }
      return data;
    } catch (error: any) {
      console.error('❌ [ORDER] Error listing backend payloads:', {
        tenantId,
        orderStateId,
        status: error.response?.status,
        message: error.message,
      });
      throw new Error('Failed to list backend payloads');
    }
  }

  static async downloadBackendPayload(
    tenantId: string,
    orderStateId: string,
    backendType: string,
    filename: string
  ): Promise<{ blob: Blob; filename: string }> {
    try {
      const normalizedType = normalizeIntegrationType(backendType);
      const params = new URLSearchParams();
      params.append('backend_type', normalizedType);
      params.append('filename', filename);

      const response: AxiosResponse<Blob> = await apiClient.get(
        `/business-documents/orders/backend-payloads/${tenantId}/${orderStateId}/download?${params.toString()}`,
        { responseType: 'blob' }
      );

      let resolvedFilename = filename;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          resolvedFilename = filenameMatch[1];
        }
      }

      return { blob: response.data, filename: resolvedFilename };
    } catch (error: any) {
      console.error('❌ [ORDER] Error downloading backend payload:', {
        tenantId,
        orderStateId,
        backendType,
        filename,
        status: error.response?.status,
        message: error.message,
      });
      throw new Error('Failed to download backend payload');
    }
  }

  static async deleteOrderState(
    tenantId: string,
    orderStateId: string,
    force: boolean = false
  ): Promise<void> {
    try {
      console.log('🗑️ [ORDER] Deleting order state:', {
        tenantId,
        orderStateId,
        force,
        url: `${API_BASE_URL}/business-documents/orders/state/${tenantId}/${orderStateId}`,
        method: 'DELETE'
      });
      
      const params = new URLSearchParams();
      if (force) {
        params.append('force', 'true');
      }
      
      const url = `/business-documents/orders/state/${tenantId}/${orderStateId}${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response: AxiosResponse<any> = await apiClient.delete(url);
      
      console.log('✅ [ORDER] Delete order state response:', {
        status: response.status,
        statusText: response.statusText
      });
    } catch (error: any) {
      console.error('❌ [ORDER] Error deleting order state:', {
        tenantId,
        orderStateId,
        force,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      
      // Re-throw with more detailed error information
      if (error.response?.data?.detail) {
        const errorMessage = new Error(error.response.data.detail);
        (errorMessage as any).status = error.response?.status;
        throw errorMessage;
      }
      throw error;
    }
  }

  static async bulkDeleteOrderStates(
    tenantId: string,
    ids: string[],
    options?: BulkDeleteOptions,
  ): Promise<BulkDeleteResult> {
    return performBulkDelete(
      apiClient,
      'ORDER',
      `/business-documents/orders/state/${tenantId}/bulk-delete`,
      ids,
      options,
    );
  }
}
